<template>
  <GeneralDialog v-model:visible="visible" @open="onModalOpen">
    <div class="w-full p-3.75">
      <div class="flex justify-between items-center mb-5 w-full">
        <span :class="$typo.openSansBold16" class="flex-1 text-text">
          {{ t('bulkModalVolumeLabel', { number: formatVolumeNumber(volumeNumber) }) }} -
          {{ entryName }}
        </span>
      </div>
      <div>
        <div v-for="(version, index) in ownedVersionsList" :key="index">
          <div class="flex gap-2.5 items-center mb-2.5">
            <span :class="$typo.openSans14">
              {{ t('selectVolumeOverviewVolumeLabel', { number: index + 1 }) }}
            </span>
            <span
              :class="$typo.openSansLight14"
              class="relative cursor-pointer min-w-[145px] px-1.5 h-6 flex items-center justify-between leading-none bg-surface rounded-[calc(var(--border-radius)*1px)] ml-auto"
              @click="() => onVolumeVersionClick(index, version)"
            >
              {{ version.suffix || t('defaultVolumeSuffixAbr') }}
              <IconCustom
                class="absolute right-0 top-px text-text-muted text-2xl leading-6"
                ligature="keyboard_arrow_down"
              />
            </span>
            <Button :icon-only="true" :destructive="true" @click="() => onVolumeDelete(index)">
              <IconCustom ligature="delete" />
            </Button>
          </div>
        </div>
        <Button class="w-full mb-4" :icon-only="true" @click="onVolumeAddClick">
          <IconCustom ligature="add" class="text-2xl leading-none" />
        </Button>
      </div>
      <div class="flex justify-between">
        <div></div>
        <div class="flex gap-2.5">
          <Button @click="dismiss">
            {{ t('collectionModalBulkEditCancel') }}
          </Button>
          <Button @click="saveVolume">
            {{ t('collectionModalBulkEditUpdate') }}
          </Button>
        </div>
      </div>
    </div>
  </GeneralDialog>
</template>

<script lang="ts" setup>
import $typo from '@/styles/typografy.module.css'
import { computed, ref, toRefs } from 'vue'
import {
  formatVolumeNumber,
  getSuffixFromVolumeVersion,
  getVolumeNumberFromVersion,
  OwnedSelectionMap,
} from '@/utils'
import { useLocalizationStore } from '@/stores/localization.ts'
import IconCustom from '@/components/IconCustom.vue'
import GeneralDialog from '@/components/GeneralDialog.vue'
import Button from '@/components/Button.vue'
import { useUiStore } from '@/stores/ui.store.ts'

const { t } = useLocalizationStore()
const { openOptionsSelect } = useUiStore()

interface Props {
  volumeNumber: string
  entryName: string
  ownedMap: OwnedSelectionMap
  sortedVolumeNumbers: string[]
}

const props = defineProps<Props>()

const { volumeNumber, sortedVolumeNumbers, ownedMap } = toRefs(props)

const emit = defineEmits<{
  (e: 'update:ownedMap', value: OwnedSelectionMap): void
}>()

const visible = defineModel('visible', { default: false })
type VolumeVersion = {
  version: string
  number: string
  suffix: string
}

function onVolumeVersionClick(index: number, version: VolumeVersion) {
  openOptionsSelect({
    label: 'selectVolumeVersion',
    options: possibleVersions.value.map((v) => ({
      translatedLabel: v.suffix || t('defaultVolumeSuffix'),
      value: v.version,
    })),
    selectedValue: version.version,
    onSave: (selectedVersion) => {
      ownedVersionsList.value = ownedVersionsList.value.map((v, i) => {
        if (i === index) {
          return possibleVersions.value.find((v) => v.version === selectedVersion)!
        }
        return v
      })
    },
  })
}

function onVolumeAddClick() {
  ownedVersionsList.value.push(possibleVersions.value.find((v) => v.number === volumeNumber.value)!)
}

const possibleVersions = computed<VolumeVersion[]>(() =>
  sortedVolumeNumbers.value
    .filter((v) => v.startsWith(volumeNumber.value))
    .map((v) => ({
      version: v,
      number: getVolumeNumberFromVersion(v),
      suffix: getSuffixFromVolumeVersion(v),
    })),
)

const ownedVersionsList = ref<VolumeVersion[]>([])

function saveVolume() {
  const newOwnedMap = {} as OwnedSelectionMap
  for (const v of possibleVersions.value) {
    newOwnedMap[v.version] = 0
  }
  for (const v of ownedVersionsList.value) {
    newOwnedMap[v.version] += 1
  }
  emit('update:ownedMap', { ...ownedMap.value, ...newOwnedMap })
  visible.value = false
}

function dismiss() {
  visible.value = false
}

function onModalOpen() {
  ownedVersionsList.value = []
  for (const v of possibleVersions.value) {
    if (ownedMap.value[v.version]) {
      for (let i = 0; i < ownedMap.value[v.version]; i++) {
        ownedVersionsList.value.push(v)
      }
    }
  }
}

function onVolumeDelete(index: number) {
  ownedVersionsList.value = ownedVersionsList.value.filter((_, i) => i !== index)
}
</script>
